// El tramo del clip que va al backend para borrar un objeto o mejorar la calidad —
// ADR-2026-11-04-el-borrado-se-cobra-y-va-por-tramo.
//
// El archivo en Storage sigue entero aunque el clip esté recortado: el backend corta
// el tramo (`media_offset_sec` + `duration_sec`) y devuelve un archivo que ES ese
// tramo, así que el clip pasa a leerlo desde 0. Mismo criterio que "Editar clip".

import { segundosDelRecorteOmni } from '../../lib/omniEdit';

/** Bria (el borrado) trabaja hasta 5 s. */
export const MAX_SEG_DEL_BORRADO = 5;

/** Lo que el backend tolera por redondeo antes de rechazar (`ventanaDeRecorte`). */
const TOLERANCIA_SEG = 0.25;

type ClipConTramo = {
  duration: number;
  speed?: number;
  mediaOffset?: number;
  sourceDuration?: number;
};

/** Los campos del tramo para el body del backend. */
export function tramoParaElBackend(clip: ClipConTramo): { media_offset_sec: number; duration_sec: number } {
  const offset =
    typeof clip.mediaOffset === 'number' && Number.isFinite(clip.mediaOffset) ? Math.max(0, clip.mediaOffset) : 0;
  return { media_offset_sec: offset, duration_sec: segundosDelRecorteOmni(clip) };
}

/** `true` si el tramo del clip no entra en el borrado: hay que dividirlo antes. */
export function excedeElTopeDelBorrado(clip: ClipConTramo): boolean {
  return segundosDelRecorteOmni(clip) > MAX_SEG_DEL_BORRADO + TOLERANCIA_SEG;
}

export const MENSAJE_BORRADO_LARGO =
  `Borrar objeto funciona con tramos de hasta ${MAX_SEG_DEL_BORRADO} s. Dividí el clip (S) y borrá cada parte.`;

/**
 * Cómo queda el clip con el archivo nuevo, que empieza en `desdeSec` del archivo viejo
 * (donde estaba el tramo cuando se pidió el trabajo) y mide `segundosDelArchivo`.
 *
 * Se respeta el recorte que el clip tenga AHORA (review Greptile #243): si se recortó
 * mientras corría el trabajo, el clip lee el mismo pedazo dentro del archivo nuevo, no
 * vuelve a crecer. Si el backend acotó el tramo (5,1 s sale de 5), el clip se ajusta a lo
 * que hay. `null` si el clip de ahora ya no cubre nada de lo que se procesó: el resultado
 * queda en Archivos y el clip no se toca.
 *
 * `desdeSec` ausente es 0: un trabajo pedido sin tramo, que el backend procesa desde el
 * principio del archivo.
 */
export function conElArchivoDelTramo(
  clip: ClipConTramo,
  segundosDelArchivo: number,
  desdeSec: number = 0,
): { mediaOffset: number; sourceDuration: number; duration?: number } | null {
  if (!Number.isFinite(segundosDelArchivo) || segundosDelArchivo <= 0) return null;
  const offsetActual =
    typeof clip.mediaOffset === 'number' && Number.isFinite(clip.mediaOffset) ? Math.max(0, clip.mediaOffset) : 0;
  const desde = Number.isFinite(desdeSec) && desdeSec > 0 ? desdeSec : 0;
  const mediaOffset = Math.max(0, offsetActual - desde);
  const disponible = segundosDelArchivo - mediaOffset;
  if (disponible <= 0.05) return null;
  const recorte = segundosDelRecorteOmni(clip);
  const material = Math.min(recorte, disponible);
  const velocidad = typeof clip.speed === 'number' && clip.speed > 0 ? clip.speed : 1;
  return {
    ...(Math.abs(material - recorte) > 0.05 ? { duration: material / velocidad } : {}),
    mediaOffset,
    sourceDuration: segundosDelArchivo,
  };
}
